export function resizeImage(url: string, w?: number, h?: number, pad?: boolean): string {
	if (!isCloudinaryUrl(url)) return url

	const prefix = url.split('/upload/')[0]
	const rest = url.split('/upload/')[1]
	if (!rest) return url

	const parts = rest.split('/')
	let transform = ''
	let version = ''
	let remaining: string[] = []

	if (parts[0].includes('.')) {
		remaining = parts
	} else if (parts[0].startsWith('v') && /^\d+$/.test(parts[0].slice(1))) {
		version = parts[0]
		remaining = parts.slice(1)
	} else {
		transform = parts[0]
		if (parts[1]?.startsWith('v') && /^\d+$/.test(parts[1].slice(1))) {
			version = parts[1]
			remaining = parts.slice(2)
		} else {
			remaining = parts.slice(1)
		}
	}

	const segments = new Set(transform.split(',').filter(Boolean))

	// Remove any existing transformations
	for (const t of Array.from(segments)) {
		if (t.startsWith('c_') || t.startsWith('w_') || t.startsWith('h_') || t.startsWith('b_') || t.startsWith('g_')) {
			segments.delete(t)
		}
	}

	// Add new size transforms
	if (pad) {
		segments.add('c_pad')
		segments.add('b_auto')
	} else {
		segments.add('c_limit')
	}
	if (w) segments.add(`w_${w}`)
	if (h) segments.add(`h_${h}`)

	// Rebuild URL with proper structure
	const finalTransform = [...segments].join(',')
	const urlParts = [prefix, 'upload']

	if (finalTransform) {
		urlParts.push(finalTransform)
	}
	if (version) {
		urlParts.push(version)
	}
	urlParts.push(...remaining)

	return urlParts.join('/')
}

export function resizeThumbnailImage(url: string) {
	return resizeImage(url, 216, 176)
}

export function isCloudinaryUrl(url: unknown): url is string {
	return typeof url === 'string' &&
		url.includes('res.cloudinary.com') &&
		url.includes('/upload/')
}

export function mergeTransform(url: string, newTransform: string): string {
	if (!isCloudinaryUrl(url)) return url

	const prefix = url.split('/upload/')[0]
	const rest = url.split('/upload/')[1]
	const parts = rest.split('/')
	let transform = ''
	let version = ''
	let remaining: string[] = []

	if (parts[0].includes('.')) {
		remaining = parts
	} else if (parts[0].startsWith('v') && /^\d+$/.test(parts[0].slice(1))) {
		version = parts[0]
		remaining = parts.slice(1)
	} else {
		transform = parts[0]
		if (parts[1]?.startsWith('v') && /^\d+$/.test(parts[1].slice(1))) {
			version = parts[1]
			remaining = parts.slice(2)
		} else {
			remaining = parts.slice(1)
		}
	}

	const segments = new Set(transform.split(',').filter(Boolean))

	if (newTransform === 'f_auto,q_auto') {
		for (const t of segments) {
			if (t.startsWith('f_') || t.startsWith('q_')) {
				segments.delete(t)
			}
		}
		segments.add('f_auto')
		segments.add('q_auto')
	} else {
		const typePrefix = newTransform.split('_')[0]
		for (const t of segments) {
			if (t.startsWith(typePrefix + '_')) {
				segments.delete(t)
			}
		}
		segments.add(newTransform)
	}

	const finalTransform = [...segments].join(',')
	const urlParts = [prefix, 'upload']

	if (finalTransform) {
		urlParts.push(finalTransform)
	}
	if (version) {
		urlParts.push(version)
	}
	urlParts.push(...remaining)

	return urlParts.join('/')
}

export function setQuality(url: string, quality: number): string {
	if (quality <= 0 || quality > 100) return url
	return mergeTransform(url, `q_${quality}`)
}

export function setWidth(url: string, width: number): string {
	if (!width || width <= 0) return url
	return mergeTransform(url, `w_${width}`)
}

export function setAutoFormat(url: string): string {
	return mergeTransform(url, 'f_auto,q_auto')
}

export function clearTransform(url: string): string {
	if (!isCloudinaryUrl(url)) return url

	const prefix = url.split('/upload/')[0]
	const rest = url.split('/upload/')[1]
	const parts = rest.split('/')
	let version = ''
	let remaining: string[] = []

	// Check if first part is a version (starts with 'v' followed by digits)
	if (parts[0].startsWith('v') && /^\d+$/.test(parts[0].slice(1))) {
		version = parts[0]
		remaining = parts.slice(1)
	} else if (parts[0].includes('.')) {
		// First part is filename (contains extension)
		remaining = parts
	} else {
		// First part is transform, check if second part is version
		if (parts[1]?.startsWith('v') && /^\d+$/.test(parts[1].slice(1))) {
			version = parts[1]
			remaining = parts.slice(2)
		} else {
			remaining = parts.slice(1)
		}
	}

	const urlParts = [prefix, 'upload']

	if (version) {
		urlParts.push(version)
	}
	urlParts.push(...remaining)

	return urlParts.join('/')
}

export function cropSegment(url: string, x: number, width: number, height: number): string {
	if (!isCloudinaryUrl(url)) return url
	if (width <= 0 || height <= 0 || x < 0) return url

	let result = clearTransform(url)
	result = mergeTransform(result, 'c_crop')
	result = mergeTransform(result, `w_${width}`)
	result = mergeTransform(result, `h_${height}`)
	result = mergeTransform(result, `x_${x}`)
	result = mergeTransform(result, 'y_0')
	return result
}

export function getFileExtension(url: string): string | null {
	const parts = url?.split?.('.') ?? []
	if (parts.length > 1) {
		return parts.pop()?.split?.(/\#|\?/)?.[0] || null
	}
	return null
}

export function changeFileExtension(url: string, newExtension: string): string {
	const urlWithoutExtension = url?.split?.('.').slice(0, -1).join('.')
	if (!urlWithoutExtension) return url
	const [baseUrl, queryFragment] = urlWithoutExtension.split(/(\?|#)/)
	return `${baseUrl}.${newExtension}${queryFragment || ''}`
}

export function bytesToSize(bytes: number, decimals = 2): string {
	if (bytes === 0) {
		return '0 Bytes'
	}

	const k = 1024
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
